import { useRouter } from "next/router"
import { useMutation } from "@blitzjs/rpc"
import { BlitzPage, Routes } from "@blitzjs/next"
import Authentication from "app/core/layouts/OnboardingV2"
import Layout from "app/core/layouts/Layout"
import { LabeledTextField } from "app/core/components/form/LabeledTextField"
import { Form, FORM_ERROR } from "app/core/components/form/Form"
import { ForgotPassword } from "app/auth/validations"
import forgotPassword from "app/auth/mutations/forgotPassword"
import { LayoutModal } from "app/core/components/layout/Modal"
import { Spacer } from "app/paywallTriggers/components/misc"
import Breadcrumbs from "app/core/components/breadcrumbs/Breadcrumb"
import { CardBlur } from "app/auth/components/LoginForm"
import { ColorBlur } from "app/splash/components/ColorBlur"
import Link from "next/link"
import tw from "tailwind-styled-components"
import useUserAgent from "app/core/hooks/useUserAgent"

export const Title = tw.div`
  -mt-1
  md:mb-[10px]
  text-white
  text-2xl
  md:text-3xl
  font-medium
`

const ForgotPasswordPage: BlitzPage = () => {
  const [forgotPasswordMutation, { isSuccess }] = useMutation(forgotPassword)
  const router = useRouter()
  const { isMobileClient } = useUserAgent()

  return (
    <CardBlur>
      <ColorBlur fixed={true} opacity={0.05}></ColorBlur>
      {isSuccess ? (
        <div>
          <Title>Check your email</Title>
          <p className="opacity-80">
            If your email is in our system, you will receive instructions to reset your password
            shortly.
          </p>
        </div>
      ) : (
        <>
          <div className="text-left mb-5">
            <Title>Password Reset</Title>
          </div>
          <Form
            submitText="Reset →"
            buttonFill={true}
            buttonPosition="end"
            buttonProps={{
              variant: "juicy",
              className: "w-auto",
            }}
            schema={ForgotPassword}
            initialValues={{ email: decodeURIComponent(router.query.email as string) || "" }}
            onSubmit={async (values) => {
              try {
                await forgotPasswordMutation(values)
              } catch (error) {
                return {
                  [FORM_ERROR]: "Sorry, we had an unexpected error. Please try again.",
                }
              }
            }}
          >
            <LabeledTextField name="email" label="Email" placeholder="Email" />
            <div className="w-full flex flex-row justify-end mb-0 relative">
              <div className="flex flex-inline gap-2 items-end text-sm self-start absolute left-0 pr-2 py-0 top-[1rem]">
                <Link passHref={true} href={Routes.LoginPage()} className="text-left">
                  <span className="flex items-center opacity-60 hover:text-brand-primary transition-all hover:opacity-100">{`← Log In`}</span>
                </Link>{" "}
                {!isMobileClient && (
                  <>
                    <span className="opacity-30"> or </span>
                    <Link passHref={true} href={Routes.SignupPage()} className="text-left">
                      <span className="flex items-center opacity-60 hover:text-brand-primary transition-all hover:opacity-100">{`Sign Up`}</span>
                    </Link>
                  </>
                )}
              </div>
            </div>
          </Form>
        </>
      )}
    </CardBlur>
  )
}

ForgotPasswordPage.redirectAuthenticatedTo = "/"
ForgotPasswordPage.getLayout = (page) => (
  <Authentication title="Forgot Your Password?">{page}</Authentication>
)

export default ForgotPasswordPage
